import { http } from '@/lib/ats-https';
import { useQuery, useMutation } from '@tanstack/react-query';

// add_document runs in a worker; poll until it reports success. The ceiling covers the worst case
// the server allows — MAX_DOCUMENTS (10) documents downloaded, re-uploaded and parsed.
const ADD_DOC_POLL_INTERVAL_MS = 2000;
const ADD_DOC_POLL_TIMEOUT_MS = 120000;

// API Functions
export const offerDetailsAPI = {
  // Create offer
  createOffer: async ({ payload }) => {
    const response = await http.post('/ats_offers', payload);
    return response;
  },
  // Update offer
  updateOffer: async ({ payload, id }) => {
    const response = await http.put(`/ats_offers/${id}`, payload);
    return response;
  },
  skipApprovalAndGenerateOffer: async ({ id }) => {
    const response = await http.put(
      `/ats_offers/${id}/update_status/return_to_pending_approval`
    );
    return response;
  },
  // Get custom fields for offers
  getCustomFields: async (params) => {
    const response = await http.get('/ats_custom_fields/form_fields', {
      params,
    });
    return response.data;
  },

  // Get offer by ID
  getOfferById: async (id) => {
    const response = await http.get(`/ats_offers/${id}`);
    return response.data;
  },

  // Get compensation currencies
  getCompensationCurrencies: async () => {
    const response = await http.get('/compensations/currencies');
    return response.data;
  },

  // Get ATS offices
  getAtsOffices: async (params = {}) => {
    const response = await http.get('/ats_offices', { params });
    return response.data;
  },

  // Get ATS departments
  getAtsDepartments: async () => {
    const response = await http.get('/ats_departments');
    return response.data?.departments;
  },

  // Get all offer templates
  getAllOfferTemplates: async (params = {}) => {
    const response = await http.get('/ats_offer_templates', { params });
    return response;
  },

  // Get approval flow by entity
  getApprovalsByEntity: async ({ entityId, entityType }) => {
    const response = await http.get(
      `approvals/approval_template?entity_id=${entityId}&type=${entityType}`
    );
    return response.data;
  },

  // Get approval settings
  getApprovalSettings: async () => {
    const response = await http.get('/approvals/settings');
    return response.data;
  },

  // Upload replacement document. For multi-doc offers, documentIndex targets one document.
  uploadReplacement: async ({ id, file, documentIndex }) => {
    const formData = new FormData();
    formData.append('offer_document', file);
    formData.append('name', 'test');
    if (documentIndex !== undefined && documentIndex !== null) {
      formData.append('document_index', documentIndex);
    }
    const response = await http.put(
      `/ats_offers/${id}/upload_replacement`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  // Append one or more documents to the offer's template as first-class offer documents
  // (token-validated + substituted on generate), like the selected template's own docs. Sending
  // the whole batch in one request means a single per-offer clone / reset / envelope-void and one
  // MAX_DOCUMENTS check (rejected wholesale, not partially applied).
  //
  // Asynchronous server-side: the request only validates and uploads the bytes, then returns
  // 202 + { poll_id }. The docx parse and the per-offer template clone run in a worker. Prefer
  // addOfferDocumentAndWait, which polls to completion and yields the updated template.
  addOfferDocument: async ({ id, files }) => {
    const formData = new FormData();
    (files || []).forEach((file) => formData.append('offer_document[]', file));
    const response = await http.put(
      `/ats_offers/${id}/add_document`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  // Poll an add_document job. { status: 'pending' | 'success' | 'failed', offer_template?, error? }
  getAddDocumentStatus: async ({ id, pollId }) => {
    const response = await http.get(`/ats_offers/${id}/add_document_status`, {
      params: { poll_id: pollId },
    });
    return response.data;
  },

  // Resolves with the updated per-offer template once the worker reports success, so callers keep
  // the plain `await -> template` shape the synchronous endpoint used to give them. Rejects with
  // the server's err_* code as the Error message; map it for display via addDocumentErrorCopy.
  addOfferDocumentAndWait: async ({ id, files }) => {
    const { poll_id: pollId } = await offerDetailsAPI.addOfferDocument({
      id,
      files,
    });
    const deadline = Date.now() + ADD_DOC_POLL_TIMEOUT_MS;

    for (;;) {
      let result;
      try {
        result = await offerDetailsAPI.getAddDocumentStatus({ id, pollId });
      } catch (error) {
        // 404 = the result expired (1h TTL) or is not bound to this offer; 400 = malformed id.
        // The append may well have succeeded, so this deliberately isn't "your upload failed".
        const status = error?.response?.status;
        throw new Error(
          status === 404 || status === 400
            ? 'err_poll_result_not_found'
            : 'err_document_append_failed'
        );
      }

      if (result?.status === 'success') return result.offer_template;
      if (result?.status === 'failed') {
        throw new Error(result.error || 'err_document_append_failed');
      }
      if (Date.now() > deadline) throw new Error('err_document_append_timeout');
      await new Promise((resolve) =>
        setTimeout(resolve, ADD_DOC_POLL_INTERVAL_MS)
      );
    }
  },

  // Remove one document from a multi-doc offer (per-offer; the shared template is untouched).
  removeOfferDocument: async ({ id, documentIndex }) => {
    const response = await http.delete(`/ats_offers/${id}/remove_document`, {
      data: { document_index: documentIndex },
    });
    return response.data;
  },

  // Generate offer document
  generateOfferDocument: async ({ id, event }) => {
    const response = await http.put(
      `ats_offers/${id}/update_status/${event}`,
      null
    );
    return response.data;
  },

  sendForApproval: async ({ id, payload = {} }) => {
    const response = await http.put(
      `/ats_offers/${id}/update_status/send_for_approval`,
      payload
    );
    return response;
  },

  // Send offer email
  sendOfferEmail: async ({ id, payload }) => {
    const response = await http.put(`/ats_offers/${id}/send_email`, payload);
    return response;
  },

  // Send offer via SpotDraft. Mirrors the RTK Query base error handling it
  // replaced (axiosAtsQuery) so both existing call sites — which branch on
  // `'error' in response` — keep working unchanged.
  sendWithSpotDraft: async ({ id, payload }) => {
    try {
      const response = await http.post(
        `ats_offers/${id}/send_spotdraft`,
        payload
      );
      return { data: response?.data };
    } catch (err) {
      return { error: err?.response?.data };
    }
  },

  // validatePreapproval: async ({ payload, offerId }) => {
  //   const response = await http.post(
  //     `/ats_offers/${offerId}/validate_preapproval`,
  //     payload
  //   );
  //   return response.data;
  // },

  // Poll offer status
  pollOfferById: async (id) => {
    const response = await http.get(`/ats_offers/${id}/poll_status`);
    return response.data;
  },

  // Upload attachment
  uploadAttachment: async ({ id, file }) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await http.put(
      `/ats_offers/${id}/upload_attachment`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response;
  },

  // Delete attachment
  deleteAttachment: async ({ offerId, attachmentId }) => {
    const response = await http.delete(
      `/ats_offers/${offerId}/delete_attachment`,
      { data: { attachment_id: attachmentId } }
    );
    return response;
  },
};

// Query Keys
export const offerDetailsKeys = {
  all: ['offer-details'],
  customFields: (params) => [...offerDetailsKeys.all, 'custom-fields', params],
  offerById: (id) => [...offerDetailsKeys.all, 'offer', id],
  currencies: () => [...offerDetailsKeys.all, 'currencies'],
  offices: (params) => [...offerDetailsKeys.all, 'offices', params],
  departments: () => [...offerDetailsKeys.all, 'departments'],
  templates: (params) => [...offerDetailsKeys.all, 'templates', params],
  // Prefix over every entity's approval flow — invalidate with this after a
  // mutation that can change who has to approve.
  approvalsAll: () => [...offerDetailsKeys.all, 'approvals'],
  approvals: (entityId, entityType) => [
    ...offerDetailsKeys.approvalsAll(),
    entityId,
    entityType,
  ],
};

// React Query Hooks
export const useGetCustomFieldsQuery = (params, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.customFields(params),
    queryFn: () => offerDetailsAPI.getCustomFields(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetOfferByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.offerById(id),
    queryFn: () => offerDetailsAPI.getOfferById(id),
    staleTime: 0,
    enabled: !!id,
    ...options,
  });
};

export const useGetCompensationCurrenciesQuery = (options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.currencies(),
    queryFn: offerDetailsAPI.getCompensationCurrencies,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAtsOfficesQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.offices(params),
    queryFn: () => offerDetailsAPI.getAtsOffices(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAtsDepartmentsQuery = (options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.departments(),
    queryFn: offerDetailsAPI.getAtsDepartments,
    staleTime: 3 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetAllOfferTemplatesQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: offerDetailsKeys.templates(params),
    queryFn: () => offerDetailsAPI.getAllOfferTemplates(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useGetApprovalsByEntityQuery = (
  { entityId, entityType },
  options = {}
) => {
  return useQuery({
    queryKey: offerDetailsKeys.approvals(entityId, entityType),
    queryFn: () =>
      offerDetailsAPI.getApprovalsByEntity({ entityId, entityType }),
    staleTime: 0,
    enabled: !!entityId && !!entityType,
    ...options,
  });
};

export const useGetApprovalSettingsQuery = (options = {}) => {
  return useQuery({
    queryKey: ['approval-settings'],
    queryFn: () => offerDetailsAPI.getApprovalSettings(),
    staleTime: 0,
    ...options,
  });
};

// Mutation Hooks
export const useUploadReplacementMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.uploadReplacement,
    ...options,
  });
};

export const useGenerateOfferDocumentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.generateOfferDocument,
    ...options,
  });
};

export const useSendForApprovalMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.sendForApproval,
    ...options,
  });
};

export const useSendOfferEmailMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.sendOfferEmail,
    ...options,
  });
};

export const useSendWithSpotDraftMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.sendWithSpotDraft,
    ...options,
  });
};

// Poll hook with custom interval
export const usePollOfferByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [...offerDetailsKeys.offerById(id), 'poll'],
    queryFn: () => offerDetailsAPI.pollOfferById(id),
    staleTime: 0,
    refetchInterval: (data) => {
      // Stop polling if document is ready or failed
      if (!data) return false;
      const status = data?.offer_status;
      if (
        status === 'pending_document_generation' ||
        status === 'document_generation_in_progress'
      ) {
        return 2000; // Poll every 2 seconds
      }
      return false; // Stop polling
    },
    enabled: !!id,
    ...options,
  });
};

// Stable key so the approval step can observe an in-flight upload without the
// picker having to hoist that state — see
// PERSIST_APPROVAL_ATTACHMENTS_MUTATION_KEY for the same pattern.
export const UPLOAD_ATTACHMENT_MUTATION_KEY = ['offer', 'upload-attachment'];

export const useUploadAttachmentMutation = (options = {}) => {
  return useMutation({
    mutationKey: UPLOAD_ATTACHMENT_MUTATION_KEY,
    mutationFn: offerDetailsAPI.uploadAttachment,
    ...options,
  });
};

export const useDeleteAttachmentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.deleteAttachment,
    ...options,
  });
};

export const useRemoveOfferDocumentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.removeOfferDocument,
    ...options,
  });
};

// Polls to completion, so isPending stays true for the whole append and onSuccess/onError fire on
// the real outcome rather than on the 202 acknowledgement.
export const useAddOfferDocumentMutation = (options = {}) => {
  return useMutation({
    mutationFn: offerDetailsAPI.addOfferDocumentAndWait,
    ...options,
  });
};

// export const useValidatePreapprovalMutation = (options = {}) => {
//   return useMutation({
//     mutationFn: offerDetailsAPI.validatePreapproval,
//     ...options,
//   });
// };
